<template>
  <div>
    <h1 data-ci="login-page-title">
      <strong v-t="`${langRoot}.title`" />
    </h1>

    <hr />

    <page-notification
      v-if="this.$route.query.error"
      data-ci="login-error-notification"
      type="error"
      :messages="[$t(`${langRoot}.genericError.title`), $t(`${langRoot}.genericError.message`)]"
    />

    <page-notification
      v-if="!isCookiesEnabled"
      type="error"
      :messages="[$t(`${langRoot}.noCookieMsg`)]"
      data-ci="login-error-notification"
    />
    <p v-t="`${langRoot}.usernameAndPasswordInstruction`" />

    <br />

    <p class="login__required-helper" v-t="`${langRoot}.requiredHelper`" />
    <form
      name="loginForm"
      class="login__form"
      :action="loginUrl"
      method="POST"
      v-on:submit.prevent="login"
      autocomplete="off"
    >
      <!-- Username -->
      <div class="field login__field is-horizontal">
        <div class="field-label is-normal">
          <label class="label" for="username-input" v-t="`${langRoot}.loginForm.username`" />
        </div>

        <div class="field-body">
          <div class="field">
            <div class="control">
              <input
                v-model="form.data.username"
                id="username-input"
                name="username"
                ref="username"
                class="input"
                type="text"
                :class="{ 'login__input': !form.field.username.hasError,
									  'login__input--error': form.field.username.hasError }"
                @change="$trackEvent(pageTag, 'enter_username')"
                data-ci="username-input"
                autocomplete="off"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Password -->
      <div class="field login__field is-horizontal">
        <div class="field-label is-normal">
          <label class="label" for="password-input" v-t="`${langRoot}.loginForm.password`" />
        </div>
        <!-- This is to prevent autocomplete on certain browsers -->
        <input type="text" style="display:none;" />
        <input type="password" style="display:none;" />
        <div class="field-body">
          <div class="field">
            <div class="control">
              <input
                id="password-input"
                v-model="form.data.password"
                name="password"
                ref="password"
                class="input login__input"
                type="password"
                v-bind:class="{ 'login__input--error': form.field.password.hasError }"
                data-ci="password-input"
              />
            </div>
          </div>
        </div>
      </div>

      <br />
      <i18n :path="`${langRoot}.forgottenUserOrPassword.question`" tag="span">
        <a
          href="#"
          @click="forgottenUsername()"
          place="usernameLink"
          v-t="`${langRoot}.forgottenUserOrPassword.username`"
          data-ci="recover-username-link"
        />
        <a
          href="#"
          @click="forgottenPassword()"
          place="passwordLink"
          v-t="`${langRoot}.forgottenUserOrPassword.password`"
          data-ci="recover-password-link"
        />
      </i18n>
      <div v-if="getInvitation()">
        <br /><br />
        <div v-if="form.feedback" class="login__form-feedback" data-ci="login-feedback">
          <i8-icon colour="gold" icon="circle-error-solid" />
          <span>{{ form.feedback }}</span>
        </div>
      </div>

      <div class="level login__footer">
        <div class="level-left level-item">
          <div v-if="!getInvitation() && form.feedback" class="login__form-feedback" data-ci="login-feedback">
            <i8-icon colour="gold" icon="circle-error-solid" />
            <span>{{ form.feedback }}</span>
          </div>

          <button v-if="getInvitation()"
            class="btn ghost"
            type="button"
            @click="goBackToRegistration()"
            data-ci="login-button"
            v-t="`${langRoot}.cancelBtn`"
          />

        </div>
        <div class="level-right level-item">
          <button
            class="btn primary"
            type="submit"
            data-ci="login-button"
            v-t="`${langRoot}.submitBtn`"
          />
        </div>
      </div>
    </form>
  </div>
</template>
<script>
import axios from 'axios';
import path from 'path';
import tracking from '@shared/mixins/tracking';
import PageNotification from '@components/PageNotification';
import I8Icon from '@shared/components/I8Icon';
import isEmpty from 'lodash/isEmpty';
import { getUrlQueryValue } from '@shared/utils';
import { API_PREFIX } from '@shared/globals';

const name = 'login-form';

let api = axios.create({
  baseURL: `/${API_PREFIX}`,
  headers: {},
  withCredentials: true,
});

export default {
    name,
    componentName: name,

    components: {
        [PageNotification.name]: PageNotification,
        [I8Icon.name]: I8Icon,
    },

    mixins: [tracking],

    data() {
        return {
            queryParams: {},
            form: {
                data: {
                    username: null,
                    password: null,
                },
                field: {
                    username: {
                        label: 'Username*',
                        hasError: false,
                    },
                    password: {
                        label: 'Password*',
                        hasError: false,
                    },
                },
                helper: {
                    emptyPassword: this.$t(`loginScreen.loginForm.emptyPassword`),
                    emptyUsername: this.$t(`loginScreen.loginForm.emptyUsername`),
                    unknownUser: this.$t(`loginScreen.loginForm.unknownUser`),
                },
                feedback: null,
            },
            langRoot: 'loginScreen',
            isCookiesEnabled: navigator.cookieEnabled,
        };
    },

    created() {
        var params = this.getQueryParams;

        if (params &&
            params.hasOwnProperty('error_code') &&
            params.error_code.indexOf('unknown_user' >= 0)
        ) {
            this.form.feedback = this.form.helper.unknownUser;
        }
    },

    computed: {
        isPasswordValid() {
            return this.form.data.password != null && this.form.data.password != '';
        },
        isUsernameValid() {
            return this.form.data.username != null && this.form.data.username != '';
        },
        isFormValid() {
            return this.isPasswordValid && this.isUsernameValid;
        },
      // modify invitation login post Url
        loginUrl() {
            let url = 'login?challenge=' + this.getChallenge();
            if (!isEmpty(this.getInvitation())) {
              // url = `${url}&invitation=${this.getInvitation()}`
              url = `login/invitation/${this.getInvitation()}`
            }
            return url;
        },
        getQueryParams() {
            // get the current url and split to separate out query string
            let url = window.location.href.replace('#/','').split('?');
            let paramObj = null;

            // do we have any query params?
            if (url.length > 1) {
                // split the query string to separate out the query params
                let params = url[1].split('&');

                paramObj = {};
                let param = '';
                params.forEach(function(p) {
                    // split the param into key and value
                    param = p.split('=');

                    // add key/val pair to obj
                    paramObj[param[0]] = param[1];
                });
            }
            return paramObj;
        },
    },
    methods: {
        login() {
            if (this.isFormValid) {
                this.$trackEvent(this.pageTag, 'login');
                document.loginForm.submit();
            } else {
                if (!this.isUsernameValid) {
                    this.form.field.username.hasError = !this.isUsernameValid;
                    this.form.field.password.hasError = !this.isPasswordValid;

                    this.form.feedback = this.form.helper.emptyUsername;
                    this.$refs.username.focus();
                } else if (!this.isPasswordValid) {
                    this.form.field.username.hasError = !this.isUsernameValid;
                    this.form.field.password.hasError = !this.isPasswordValid;

                    this.form.feedback = this.form.helper.emptyPassword;
                    this.$refs.password.focus();
                }
            }
        },
      getChallenge() {
        // if (getUrlQueryValue('challenge')) {
        //   return String.trim(getUrlQueryValue('challenge'))
        // } else {
          return ''
        // }
        // return String.trim(document.getElementById('challenge').innerHTML);
      },
        getInvitation() {
          if (getUrlQueryValue('invitation')) {
            return String.trim(getUrlQueryValue('invitation'))
          } else {
            return ''
          }
          // return String.trim(document.getElementById("invitation").innerHTML);
        },
        forgottenUsername() {
            this.$trackEvent(this.pageTag, 'recover_username');
            this.$router.push({ name: 'recoveruser' });
        },
        forgottenPassword() {
            this.$trackEvent(this.pageTag, 'recover_password');
            this.$router.push({ name: 'passwordreset' });
        },

        goBackToRegistration(){
          api.get(path.join('api/session/clear')).then(() => {
            document.location=`/${API_PREFIX}/invitation/` + this.getInvitation();
          })

        },
    },
};
</script>



// WEBPACK FOOTER //
// login/Components/LoginForm.vue